<template>
  <ion-app>
    <Teleport to="#modal">
      <UpdateModal :show="show" />
    </Teleport>
    <ion-router-outlet animated="false" />
  </ion-app>
</template>

<script>
import { IonApp, IonRouterOutlet, useBackButton, isPlatform } from '@ionic/vue';
import { ref, onMounted, watch, computed, defineComponent } from 'vue';
import { App as CapApp } from '@capacitor/app';
import { useAuth0 } from '@auth0/auth0-vue';
import { Browser } from '@capacitor/browser';
import { unsubscribePush } from './utils/push-notification';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { redirectDeepLinkPath } from '@/utils/redirect-uri';
import { redirectShortLinkPath } from '@/utils/deeplink';
import router from './router';
import { useStore } from 'vuex';
import UpdateModal from '../src/components/UpdateModal.vue';
import { checkForceUpdate } from './utils/versionCheck';
import { useModal } from './composables/useModal';

export default defineComponent({
  name: 'App',
  components: {
    IonApp,
    IonRouterOutlet,
    UpdateModal,
  },

  setup() {
    useBackButton(10, () => {
      // leave empty to not trigger any back button event
    });

    const { handleRedirectCallback } = useAuth0();
    const isNative = isPlatform('android') || isPlatform('ios');
    const isNativeBrowser = isPlatform('mobileweb');

    const store = useStore();
    const { show } = useModal();
    const currentVersion = ref(localStorage.getItem('cached_app_version') || '');
    const minVersion = computed(() => store.getters['appVersion/getMinAppVersion']);

    onMounted(async () => {
      await store.dispatch('appVersion/fetchMinimumAppVersion');
    });

    watch(
      minVersion,
      newMinVersion => {
        if (newMinVersion && newMinVersion !== '0.0.0' && isNative) {
          checkForceUpdate(currentVersion, newMinVersion, show);
        }
      },
      { immediate: true },
    );

    CapApp.addListener('appUrlOpen', async ({ url }) => {
      const needRedirect = await redirectDeepLinkPath(router, url, store);
      if (needRedirect) {
        try {
          await Browser.close();
        } catch (error) {
          console.log(error);
        }
        return;
      }

      // Short links (hogc.link/…) arrive as the tapped short URL — resolve the
      // slug via the API instead of following the short.io redirect.
      const shortLinkRedirect = await redirectShortLinkPath(router, url, store);
      if (shortLinkRedirect) {
        try {
          await Browser.close();
        } catch (error) {
          console.log(error);
        }
        return;
      }

      let appState = null;
      if (url.includes('state') && (url.includes('code') || url.includes('error'))) {
        try {
          const redirect = await handleRedirectCallback(url);
          appState = redirect.appState;
        } catch (e) {
          if (e.error === 'access_denied') {
            if (e.error_description === 'access denied') {
              window.location.href = '/access-denied';
            }
          } else {
            throw e;
          }
        }
      }
      try {
        await Browser.close();
      } catch (error) {
        console.log(error);
      }

      if (url.includes('/callback')) {
        //init push notifications here
        if (isNative && !isNativeBrowser) {
          // pushInit();

          /**
           * Platform: Web/Android/iOS
           * Sets whether analytics collection is enabled for this app on this device.
           * @param name - enabled - boolean true/false
           * @returns void
           */
          FirebaseAnalytics.setCollectionEnabled({
            enabled: true,
          });

          //init firebase analytics here
          // if (isIosNativeOnly){
          // /**
          //  * Platform: iOS
          //  * Configure and initialize the firebase app.
          //  * @param options - firebase web app configuration options
          //  * */
          // FirebaseAnalytics.initializeFirebase({
          //   apiKey: "AIzaSyBergRxyzcQN6navwYVq2cyDwKUMm7Y7jw",
          //   // authDomain: "...",
          //   // databaseURL: "...",
          //   projectId: "gens-conf-mobile-2023",
          //   storageBucket: "gens-conf-mobile-2023.appspot.com",
          //   messagingSenderId: "641254926110",
          //   appId: "1:641254926110:ios:c0cdd848109e8797c88b0f",
          //   // measurementId: "...",
          // });
          // }
          // else {
          //   /**
          //    * Platform: Android
          //    * Configure and initialize the firebase app.
          //    * @param options - firebase web app configuration options
          //    * */
          //    FirebaseAnalytics.initializeFirebase({
          //     apiKey: "AIzaSyDs8LMEg2pLeWiLdlz8PVC5gULqsQDFVcg",
          //     // authDomain: "...",
          //     // databaseURL: "...",
          //     projectId: "gens-conf-mobile-2023",
          //     storageBucket: "gens-conf-mobile-2023.appspot.com",
          //     messagingSenderId: "641254926110",
          //     appId: "1:641254926110:android:f2bdb3c1658b6f6bc88b0f",
          //     // measurementId: "...",
          //   });
          // }
          if (appState && appState.fullPathObj) {
            // The deep link is now consumed — clear its sessionStorage source
            // so a later re-login (e.g. after a 401 logout) can't replay it.
            // Cleared HERE, not at read time in login(): iOS browserFinished
            // re-runs login() after the user taps Done, and that retry must
            // still find the stored path.
            sessionStorage.removeItem('deepLinkPathObj');
            router.push({
              path: appState.fullPathObj.path,
              query: appState.fullPathObj.query,
              hash: appState.fullPathObj.hash,
            });
          }
        }
      } else if (url.includes('/logout')) {
        window.location.href = '/';
        unsubscribePush();
        /**
         * Platform: Web/Android/iOS
         * Sets whether analytics collection is enabled for this app on this device.
         * @param name - enabled - boolean true/false
         * @returns void
         */
        FirebaseAnalytics.setCollectionEnabled({
          enabled: false,
        });
      }
    });

    // Re-check version when app returns to foreground
    CapApp.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        await store.dispatch('appVersion/fetchMinimumAppVersion');
        checkForceUpdate(currentVersion, minVersion.value, show);
      }
    });

    // const token = getAccessTokenSilently();
    // return{
    //   setToken: async() => await store.commit('user/setToken', token)
    // }

    return {
      show,
      currentVersion,
    };
  },
});
</script>
